/**
 * DSD-DSD 대사 결과 헤더
 *
 * Figma: Design System 0.1 > DSD > 기본 / 스크롤
 *
 * Row 1: 세그먼트 탭 (합계 불일치 / 변경 사항) + 액션 버튼 (코멘트, 다운로드)
 * Row 2 (showFilenames): 보고서명 (source / target)
 */

import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { Download } from "lucide-react";

// ============================================
// 타입
// ============================================

export type DsdDsdViewMode =
  | "default"
  | "sum_diffs"
  | "changes"
  | "sum_diffs_and_changes";

/** 합계불일치 필터 활성 여부 */
export const isSumDiffsActive = (mode: DsdDsdViewMode) =>
  mode === "sum_diffs" || mode === "sum_diffs_and_changes";

/** 변경사항 필터 활성 여부 */
export const isChangesActive = (mode: DsdDsdViewMode) =>
  mode === "changes" || mode === "sum_diffs_and_changes";

// ============================================
// 세그먼트 탭
// ============================================

interface SegmentTabProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

const SegmentTab = ({ active, label, onClick }: SegmentTabProps) => (
  <button
    onClick={onClick}
    className={cn(
      "relative h-8 inline-flex items-center gap-1 px-2.5 rounded-small overflow-hidden label-1-normal-medium whitespace-nowrap",
      active
        ? "bg-primary-base text-static-white"
        : "border border-line-normal text-label-normal",
    )}
  >
    {label}
    {/* Decorate/Interaction/Normal overlay */}
    <div
      className={cn(
        "absolute rounded-small pointer-events-none transition-opacity",
        "opacity-0 hover:opacity-6 active:opacity-12",
        active ? "inset-0 bg-static-white" : "-inset-px bg-decorate-neutral",
      )}
    />
  </button>
);

// ============================================
// CompareHeader
// ============================================

export interface CompareHeaderProps {
  /** 현재 뷰 모드 */
  mode: DsdDsdViewMode;
  /** 모드 변경 핸들러 */
  onModeChange: (mode: DsdDsdViewMode) => void;
  /** 합계 불일치 개수 */
  sumDiffsCount: number;
  /** 변경 사항 개수 */
  changesCount: number;
  /** Source 파일명 */
  srcFilename: string;
  /** Target 파일명 */
  trgFilename: string;
  /** 다운로드 클릭 */
  onDownload?: () => void;
  /** 코멘트 클릭 */
  onComment?: () => void;
  /** 코멘트 뱃지 표시 */
  hasCommentBadge?: boolean;
  /** 스크롤 다운 시 헤더 숨김 */
  hidden?: boolean;
}

/** 합계불일치 토글 시 다음 mode 계산 */
const toggleSumDiffs = (current: DsdDsdViewMode): DsdDsdViewMode => {
  if (isSumDiffsActive(current)) {
    return isChangesActive(current) ? "changes" : "default";
  }
  return isChangesActive(current) ? "sum_diffs_and_changes" : "sum_diffs";
};

/** 변경사항 토글 시 다음 mode 계산 */
const toggleChanges = (current: DsdDsdViewMode): DsdDsdViewMode => {
  if (isChangesActive(current)) {
    return isSumDiffsActive(current) ? "sum_diffs" : "default";
  }
  return isSumDiffsActive(current) ? "sum_diffs_and_changes" : "changes";
};

export const CompareHeader = ({
  mode,
  onModeChange,
  sumDiffsCount,
  changesCount,
  srcFilename,
  trgFilename,
  onDownload,
  onComment,
  hasCommentBadge = false,
  hidden = false,
}: CompareHeaderProps) => {
  return (
    <div className="relative shrink-0 z-20" data-dl-compare-header>
      {/* Row 1: 필터 토글 + 액션 */}
      <div className="flex items-center justify-between bg-background-gray-normal border-b border-line-normal">
        <div className="flex items-center gap-2 px-5 py-3">
          <SegmentTab
            active={mode === "default"}
            label="전체"
            onClick={() => onModeChange("default")}
          />
          <SegmentTab
            active={isSumDiffsActive(mode)}
            label={`합계 불일치 (${sumDiffsCount})`}
            onClick={() => onModeChange(toggleSumDiffs(mode))}
          />
          <SegmentTab
            active={isChangesActive(mode)}
            label={`변경 사항 (${changesCount})`}
            onClick={() => onModeChange(toggleChanges(mode))}
          />
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center gap-2 px-4 py-3">
          <Button
            variant="outlined"
            color="primary"
            size={32}
            leftIcon={<Download />}
            onClick={onDownload}
          >
            다운로드
          </Button>
        </div>
      </div>

      {/* Row 2: 보고서명 — absolute로 콘텐츠 위에 오버레이하여 레이아웃 영향 제거 */}
      <div
        className={cn(
          "absolute left-0 right-0 top-full overflow-hidden",
          hidden && "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]",
            hidden
              ? "-translate-y-full opacity-0"
              : "translate-y-0 opacity-100",
          )}
        >
          <div className="flex items-center gap-6 bg-background-gray-normal px-5 py-2 border-b border-line-normal">
            <div className="flex-1 min-w-0 flex items-center justify-center px-3 py-1">
              <span className="label-1-normal-semibold text-label-normal truncate">
                {srcFilename}
              </span>
            </div>
            <div className="flex-1 min-w-0 flex items-center justify-center px-3 py-1">
              <span className="label-1-normal-semibold text-label-normal truncate">
                {trgFilename}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
